import React from 'react';
import { useNavigate } from '@tanstack/react-router';
import { ArrowsClockwise } from '@phosphor-icons/react';
import { Button } from '@chatsdet/ui';
import {
  ScheduleExploratoryModal,
  useExploratoryTestRuns,
  useRerunExploratoryTest,
} from '../../features/exploratory-tests';
import type { ExploratoryTestRunSummary } from '../../features/exploratory-tests/types';
import { RunResultBars, type RunBarSegment } from '../components/RunResultBars';
import { deriveHeaderVerdict, type PlanItem } from './ExploratoryTestVerdict';

/**
 * Project an exploratory run onto the brand run-bar model. `completed` runs
 * carry no intrinsic pass/fail, so we derive the verdict from the report (the
 * same p0-driven rule as the header chip via `deriveHeaderVerdict`); in-flight
 * and lifecycle states map directly. Returns the bar color + a human label
 * (used for both the tooltip and the accessible name).
 *
 * @internal exported for unit tests; the header strip is the only production caller.
 */
export function mapRunToBar(run: ExploratoryTestRunSummary): {
  result: RunBarSegment['result'];
  label: string;
} {
  switch (run.status) {
    case 'pending':
    case 'planning':
    case 'running':
      return { result: 'running', label: 'Running' };
    case 'canceled':
      return { result: 'skip', label: 'Canceled' };
    case 'failed':
      return { result: 'fail', label: 'Failed' };
    case 'completed': {
      const verdict = deriveHeaderVerdict(
        (run.reportJson?.testPlan ?? []) as PlanItem[],
        (run.reportJson?.appended ?? []) as PlanItem[]
      );
      if (verdict === 'fail') return { result: 'fail', label: 'Failed' };
      if (verdict === 'blocked') return { result: 'skip', label: 'Blocked' };
      return { result: 'pass', label: 'Passed' };
    }
    default:
      return { result: 'idle', label: 'Unknown' };
  }
}

type ExploratoryRunHistoryControlsProps = {
  parentId: string | null;
  currentRunId: string;
  isInFlight: boolean;
};

export function ExploratoryRunHistoryControls({
  parentId,
  currentRunId,
  isInFlight,
}: ExploratoryRunHistoryControlsProps) {
  const navigate = useNavigate();
  const rerunMutation = useRerunExploratoryTest();
  const runsQuery = useExploratoryTestRuns(parentId ?? undefined);

  if (!parentId) return null;

  // Oldest → newest (left → right), matching the brand strip. The API already
  // returns oldest-first, so no reverse is needed.
  const runSegments: RunBarSegment[] = (runsQuery.data?.runs ?? []).map((run) => {
    const { result, label } = mapRunToBar(run);
    const isActive = run.id === currentRunId;
    return {
      id: run.id,
      result,
      isActive,
      ariaLabel: `Open ${label} run${isActive ? ' (current)' : ''}`,
      tooltip: `${isActive ? 'Current run · ' : ''}${label}`,
    };
  });

  return (
    <>
      {runSegments.length > 0 && (
        <RunResultBars
          segments={runSegments}
          onSelect={(id) =>
            navigate({
              to: '/exploratory/$exploratoryTestId',
              params: { exploratoryTestId: id },
            })
          }
        />
      )}
      <ScheduleExploratoryModal parentId={parentId} />
      {!isInFlight && (
        <Button
          variant="icon"
          size="sm"
          iconOnly
          tooltip="Re-run from prompt + context"
          iconLeft={<ArrowsClockwise size={16} />}
          onPress={() => rerunMutation.mutate(parentId)}
          isPending={rerunMutation.isPending}
        />
      )}
    </>
  );
}
